"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { getSiteContent } from "@/content";
import { type Locale, localizedPath } from "@/lib/site";

declare global {
  interface Window {
    THREE?: any;
    __orcaparcelThreeLoader?: Promise<void>;
  }
}

function loadThreeRuntime() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (window.THREE) {
    return Promise.resolve();
  }

  if (!window.__orcaparcelThreeLoader) {
    window.__orcaparcelThreeLoader = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-orcaparcel-three="true"]');
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Three.js failed to load")), { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";
      script.async = true;
      script.dataset.orcaparcelThree = "true";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Three.js failed to load"));
      document.head.appendChild(script);
    });
  }

  return window.__orcaparcelThreeLoader;
}

export function RegisterPage({ locale }: { locale: Locale }) {
  const t = getSiteContent(locale).pages.auth.register;
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function sendCode() {
    setError("");
    setMessage("");
    setCodeLoading(true);
    try {
      const response = await fetch("/api/auth/send-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, purpose: "REGISTER" })
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error?.message || "验证码发送失败");
      }
      setMessage("验证码已发送，请查收邮箱。");
    } catch (err) {
      setError(err instanceof Error ? err.message : "验证码发送失败");
    } finally {
      setCodeLoading(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code, password, confirmPassword, inviteCode, agreed })
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error?.message || "注册失败");
      }
      router.push(localizedPath(locale, "user/dashboard"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "注册失败");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-margin-mobile pb-section-gap pt-[156px] md:px-margin-desktop md:pt-[168px]">
      <div className="form-shadow flex w-full max-w-[1100px] flex-col overflow-hidden rounded-xl bg-surface-container-lowest md:min-h-[760px] md:flex-row">
        <section className="relative hidden w-full items-center justify-center overflow-hidden bg-primary p-8 md:flex md:w-1/2 md:p-10">
          <RegisterLightVisual />
          <div className="relative z-10 max-w-lg">
            <div className="mb-stack-lg">
              <span className="font-display-lg text-display-lg-mobile font-bold tracking-tighter text-white md:text-display-lg">{t.visualBrand}</span>
              <div className="mt-2 h-1.5 w-16 bg-parcel-orange" />
            </div>
            <h2 className="auth-visual-title mb-stack-md text-white">
              {t.visualTitleLines[0]}<br />
              {t.visualTitleLines[1]}
            </h2>
            <p className="mb-12 font-body-lg text-white/80">
              {t.visualCopy}
            </p>
            <div className="grid grid-cols-1 gap-gutter sm:grid-cols-2">
              {t.features.map(([icon, label, text]) => (
                <article className="rounded-xl border border-white/20 bg-white/5 p-stack-md backdrop-blur-md transition-all duration-300 hover:bg-white/15" key={label}>
                  <span className="material-symbols-outlined mb-2 text-3xl text-parcel-orange">{icon}</span>
                  <h3 className="mb-1 font-label-md text-label-md text-white">{label}</h3>
                  <p className="text-body-sm text-white/70">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="flex w-full items-center justify-center bg-glacier-white p-margin-mobile md:w-1/2 md:p-margin-desktop">
          <div className="w-full max-w-md">
            <div className="mb-stack-lg">
              <h1 className="mb-2 font-headline-lg text-headline-lg text-primary">{t.formTitle}</h1>
              <p className="font-body-md text-on-surface-variant">{t.formCopy}</p>
            </div>
            <form className="space-y-stack-md" onSubmit={submit}>
              <div className="space-y-4">
                <AuthField autoComplete="email" icon="person" label={t.fields.account[0]} onChange={setEmail} placeholder={t.fields.account[1]} type="email" value={email} />
                <div>
                  <label className="mb-1 block font-label-md text-label-md text-on-surface-variant">{t.fields.code[0]}</label>
                  <div className="flex gap-3">
                    <div className="register-field-control flex-grow group">
                      <span className="material-symbols-outlined register-field-icon group-focus-within:text-secondary">verified_user</span>
                      <input className="register-field-input" inputMode="numeric" onChange={(event) => setCode(event.target.value)} placeholder={t.fields.code[1]} type="text" value={code} />
                    </div>
                    <button className="whitespace-nowrap rounded-lg border border-outline-variant px-4 py-3 font-label-md text-on-surface-variant transition hover:border-secondary hover:text-secondary disabled:cursor-not-allowed disabled:opacity-60" type="button" disabled={codeLoading || !email} onClick={sendCode}>
                      {codeLoading ? "发送中" : t.fields.code[2]}
                    </button>
                  </div>
                </div>
                <AuthField autoComplete="new-password" icon="lock" label={t.fields.password[0]} onChange={setPassword} placeholder={t.fields.password[1]} type="password" value={password} />
                <AuthField autoComplete="new-password" icon="lock_reset" label={t.fields.confirmPassword[0]} onChange={setConfirmPassword} placeholder={t.fields.confirmPassword[1]} type="password" value={confirmPassword} />
                <AuthField icon="redeem" label={t.fields.invite[0]} onChange={setInviteCode} placeholder={t.fields.invite[1]} value={inviteCode} />
              </div>
              <label className="flex items-start gap-2 pt-2">
                <input checked={agreed} className="mt-1 cursor-pointer rounded text-parcel-orange focus:outline-none focus:ring-0" onChange={(event) => setAgreed(event.target.checked)} type="checkbox" />
                <span className="text-body-sm text-on-surface-variant">
                  {t.agreementPrefix}{" "}
                  {t.agreementLinks.map(([label, path], index) => (
                    <span key={path}>
                      {index === 2 ? t.agreementAnd : index > 0 ? t.agreementSeparator : null}
                      <Link className="font-medium text-secondary hover:underline" href={localizedPath(locale, path)}>{label}</Link>
                    </span>
                  ))}
                </span>
              </label>
              <button className="w-full rounded-2xl bg-parcel-orange py-4 font-headline-md text-white shadow-lg shadow-parcel-orange/20 transition-all duration-300 hover:bg-parcel-orange/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70" disabled={loading} type="submit">
                {loading ? "注册中..." : t.submit}
              </button>
              {message ? <p className="typo-status-tag rounded-xl bg-success/10 px-4 py-3 text-center text-success">{message}</p> : null}
              {error ? <p className="typo-status-tag rounded-xl bg-error-container px-4 py-3 text-center text-error">{error}</p> : null}
              <div className="pt-4 text-center">
                <p className="text-body-md text-on-surface-variant">
                  {t.loginPrompt} <Link className="font-semibold text-secondary hover:underline" href={localizedPath(locale, "login")}>{t.loginLink}</Link>
                </p>
              </div>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}

function RegisterLightVisual() {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    let active = true;
    let animationFrame = 0;
    let teardown: (() => void) | undefined;

    loadThreeRuntime()
      .then(() => {
        const container = containerRef.current;
        const THREE = window.THREE;

        if (!active || !container || !THREE) {
          return;
        }

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(52, 1, 0.1, 1000);
        camera.position.z = 150;

        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.domElement.setAttribute("aria-hidden", "true");
        renderer.domElement.className = "register-light-canvas";
        container.appendChild(renderer.domElement);

        const lightGroup = new THREE.Group();
        scene.add(lightGroup);

        const createPointCloud = (count: number, radius: number, color: number, size: number, opacity: number) => {
          const positions = new Float32Array(count * 3);
          for (let index = 0; index < count; index += 1) {
            const angle = index * 0.38;
            const drift = Math.sin(index * 1.7) * 18;
            const ring = radius + Math.sin(index * 0.71) * 34;
            positions[index * 3] = Math.cos(angle) * ring;
            positions[index * 3 + 1] = Math.sin(angle * 0.82) * (ring * 0.58) + drift;
            positions[index * 3 + 2] = Math.sin(angle) * 22;
          }

          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
          const material = new THREE.PointsMaterial({
            color,
            size,
            transparent: true,
            opacity,
            depthWrite: false,
            blending: THREE.AdditiveBlending
          });
          const cloud = new THREE.Points(geometry, material);
          lightGroup.add(cloud);
          return cloud;
        };

        const cyanCloud = createPointCloud(150, 62, 0x00a6d6, 2.4, 0.38);
        const blueCloud = createPointCloud(120, 96, 0x005bac, 1.8, 0.26);
        const orangeCloud = createPointCloud(44, 78, 0xff8a00, 2.8, 0.34);

        const glowGeometry = new THREE.SphereGeometry(52, 32, 32);
        const glowMaterial = new THREE.MeshBasicMaterial({
          color: 0x00a6d6,
          transparent: true,
          opacity: 0.08,
          depthWrite: false,
          blending: THREE.AdditiveBlending
        });
        const glow = new THREE.Mesh(glowGeometry, glowMaterial);
        glow.scale.set(2.2, 1.2, 0.36);
        lightGroup.add(glow);

        const resize = () => {
          const width = Math.max(container.clientWidth, 320);
          const height = Math.max(container.clientHeight, 520);
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
          renderer.setSize(width, height, false);
        };

        resize();
        window.addEventListener("resize", resize);

        const animate = () => {
          animationFrame = window.requestAnimationFrame(animate);
          const time = Date.now() * 0.001;

          lightGroup.rotation.z = Math.sin(time * 0.18) * 0.08;
          cyanCloud.rotation.y += 0.0018;
          blueCloud.rotation.y -= 0.0012;
          orangeCloud.rotation.y += 0.0024;
          glow.scale.set(2.15 + Math.sin(time * 0.9) * 0.08, 1.18 + Math.cos(time * 0.7) * 0.05, 0.36);

          renderer.render(scene, camera);
        };

        animate();

        teardown = () => {
          window.cancelAnimationFrame(animationFrame);
          window.removeEventListener("resize", resize);
          scene.traverse((object: any) => {
            object.geometry?.dispose?.();
            if (Array.isArray(object.material)) {
              object.material.forEach((material: any) => material.dispose?.());
            } else {
              object.material?.dispose?.();
            }
          });
          renderer.dispose();
          renderer.domElement.remove();
        };
      })
      .catch(() => {
        // Static CSS glow remains visible if the CDN is unavailable.
      });

    return () => {
      active = false;
      teardown?.();
    };
  }, []);

  return <div className="register-light-visual" ref={containerRef} aria-hidden="true" />;
}

export function LoginPage({ locale }: { locale: Locale }) {
  const t = getSiteContent(locale).pages.auth.login;
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, remember })
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error?.message || "登录失败");
      }
      router.push(searchParams.get("redirect") || localizedPath(locale, "user/dashboard"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-margin-mobile pb-section-gap pt-[156px] md:px-margin-desktop md:pt-[168px]">
      <div className="form-shadow flex w-full max-w-[1100px] flex-col overflow-hidden rounded-xl bg-surface-container-lowest md:min-h-[700px] md:flex-row">
        <section className="relative hidden w-full flex-col justify-between overflow-hidden bg-[#001736] p-8 md:flex md:w-1/2 md:p-10">
          <LoginNetworkVisual />
          <div className="relative z-10 flex h-full flex-col pt-8">
            <div className="mb-10 space-y-6">
              <h1 className="font-display-lg text-display-lg-mobile font-extrabold leading-tight text-white drop-shadow-lg md:text-display-lg">
                {t.visualTitleLines[0]}<br />
                {t.visualTitleLines[1]}
              </h1>
              <p className="max-w-md text-body-lg font-medium text-white opacity-90 drop-shadow-md">
                {t.visualCopy}
              </p>
            </div>

            <div className="mb-auto">
              <div className="inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-4 py-2 shadow-sm backdrop-blur-md">
                <div className="flex -space-x-2">
                  <span className="h-8 w-8 rounded-full border-2 border-primary-container bg-surface-variant" />
                  <span className="h-8 w-8 rounded-full border-2 border-primary-container bg-secondary-container" />
                  <span className="h-8 w-8 rounded-full border-2 border-primary-container bg-outline-variant" />
                </div>
                <span className="text-label-sm font-medium text-glacier-white">{t.pill}</span>
              </div>
            </div>

            <div className="mt-10">
              <div className="rounded-xl border border-white/20 bg-white/10 p-6 shadow-xl backdrop-blur-xl">
                <div className="mb-4 flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border-2 border-parcel-orange/50 bg-primary-container">
                    <span className="material-symbols-outlined text-parcel-orange">support_agent</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">{t.reminderTitle}</p>
                    <p className="text-sm text-white/70">{t.reminderStage}</p>
                  </div>
                </div>
                <p className="text-body-md font-medium italic leading-relaxed text-white">
                  {t.reminderCopy}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="flex w-full flex-col justify-center bg-surface p-8 md:w-1/2 md:p-16">
          <div className="mx-auto w-full max-w-[400px]">
            <div className="mb-10 text-center md:text-left">
              <h1 className="mb-2 text-headline-lg text-primary-container">{t.formTitle}</h1>
              <p className="text-body-md text-on-tertiary-fixed">{t.formCopy}</p>
            </div>
            <form className="space-y-6" onSubmit={submit}>
              <AuthBox autoComplete="email" icon="person" label={t.account[0]} onChange={setEmail} placeholder={t.account[1]} value={email} />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-label-md text-on-surface">{t.passwordLabel}</label>
                  <Link className="text-label-sm text-secondary hover:underline" href={localizedPath(locale, "forgot-password")}>
                    {t.forgotPassword}
                  </Link>
                </div>
                <div className="login-field-control login-field-control-with-action group">
                  <span className="material-symbols-outlined login-field-icon group-focus-within:text-secondary">lock</span>
                  <input autoComplete="current-password" className="login-field-input" onChange={(event) => setPassword(event.target.value)} placeholder={t.passwordPlaceholder} type={showPassword ? "text" : "password"} value={password} />
                  <button className="login-password-toggle" onClick={() => setShowPassword((value) => !value)} type="button">
                    <span className="material-symbols-outlined">{showPassword ? "visibility_off" : "visibility"}</span>
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-3">
                <input checked={remember} className="h-5 w-5 cursor-pointer rounded border-outline-variant text-parcel-orange focus:outline-none focus:ring-0" onChange={(event) => setRemember(event.target.checked)} type="checkbox" />
                <span className="cursor-pointer select-none text-body-sm text-on-surface-variant">{t.remember}</span>
              </label>
              <button className="flex w-full items-center justify-center gap-2 rounded-xl bg-parcel-orange py-4 font-bold text-white shadow-lg shadow-parcel-orange/20 transition-all hover:-translate-y-0.5 hover:bg-parcel-orange/90 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-70" disabled={loading} type="submit">
                <span>{loading ? "登录中..." : t.submit}</span>
                <span className="material-symbols-outlined">login</span>
              </button>
              {error ? <p className="typo-status-tag rounded-xl bg-error-container px-4 py-3 text-center text-error">{error}</p> : null}
              <div className="flex items-center gap-4 py-4">
                <div className="h-px flex-1 bg-outline-variant" />
                <span className="text-label-sm uppercase tracking-widest text-outline">{t.divider}</span>
                <div className="h-px flex-1 bg-outline-variant" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <button className="flex items-center justify-center gap-3 rounded-lg border border-outline-variant py-3 opacity-60" type="button" disabled title={t.unavailableNotice}>
                  <span className="login-social-mark bg-[#34435E] text-white">G</span>
                  <span className="text-label-md text-on-surface">{t.google}</span>
                </button>
                <button className="flex items-center justify-center gap-3 rounded-lg border border-outline-variant py-3 opacity-60" type="button" disabled title={t.unavailableNotice}>
                  <span className="login-social-mark bg-[#2F8F84] text-white">
                    <span className="material-symbols-outlined !text-[14px]">chat</span>
                  </span>
                  <span className="text-label-md text-on-surface">{t.wechat}</span>
                </button>
              </div>
              <div className="pt-4 text-center">
                <p className="text-body-sm text-text-secondary">
                  {t.registerPrompt} <Link className="font-semibold text-on-secondary-fixed-variant hover:underline" href={localizedPath(locale, "register")}>{t.registerLink}</Link>
                </p>
              </div>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}

export function ForgotPasswordPage({ locale }: { locale: Locale }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error?.message || "验证码发送失败");
      }
      setMessage("验证码已发送，请查收邮箱后继续重置密码。");
    } catch (err) {
      setError(err instanceof Error ? err.message : "验证码发送失败");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-margin-mobile pb-section-gap pt-[156px] md:px-margin-desktop md:pt-[168px]">
      <section className="form-shadow w-full max-w-[560px] rounded-xl bg-surface-container-lowest p-margin-mobile md:p-margin-desktop">
        <h1 className="mb-3 text-headline-lg text-primary-container">忘记密码</h1>
        <p className="mb-8 text-body-md text-on-surface-variant">请输入注册邮箱，我们会发送 6 位验证码用于重置密码。</p>
        <form className="space-y-5" onSubmit={submit}>
          <AuthBox autoComplete="email" icon="mail" label="电子邮箱" onChange={setEmail} placeholder="请输入注册邮箱" value={email} />
          <button className="w-full rounded-xl bg-parcel-orange py-4 font-bold text-white shadow-lg shadow-parcel-orange/20 disabled:opacity-70" disabled={loading} type="submit">
            {loading ? "发送中..." : "发送验证码"}
          </button>
          {message ? <p className="rounded-xl bg-success/10 px-4 py-3 text-center text-sm font-semibold text-success">{message}</p> : null}
          {error ? <p className="rounded-xl bg-error-container px-4 py-3 text-center text-sm font-semibold text-error">{error}</p> : null}
          <div className="flex items-center justify-between pt-2 text-sm">
            <Link className="font-semibold text-secondary hover:underline" href={`${localizedPath(locale, "reset-password")}?email=${encodeURIComponent(email)}`}>我已收到验证码</Link>
            <Link className="text-on-surface-variant hover:text-secondary" href={localizedPath(locale, "login")}>返回登录</Link>
          </div>
        </form>
      </section>
    </main>
  );
}

export function ResetPasswordPage({ locale }: { locale: Locale }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code, password, confirmPassword })
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error?.message || "重置失败");
      }
      router.push(`${localizedPath(locale, "login")}?reset=success`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "重置失败");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-margin-mobile pb-section-gap pt-[156px] md:px-margin-desktop md:pt-[168px]">
      <section className="form-shadow w-full max-w-[560px] rounded-xl bg-surface-container-lowest p-margin-mobile md:p-margin-desktop">
        <h1 className="mb-3 text-headline-lg text-primary-container">重置密码</h1>
        <p className="mb-8 text-body-md text-on-surface-variant">请输入邮箱验证码并设置新密码。</p>
        <form className="space-y-5" onSubmit={submit}>
          <AuthBox autoComplete="email" icon="mail" label="电子邮箱" onChange={setEmail} placeholder="请输入注册邮箱" value={email} />
          <AuthBox icon="verified_user" label="验证码" onChange={setCode} placeholder="请输入6位验证码" value={code} />
          <AuthField autoComplete="new-password" icon="lock" label="新密码" onChange={setPassword} placeholder="8-64位字符" type="password" value={password} />
          <AuthField autoComplete="new-password" icon="lock_reset" label="确认新密码" onChange={setConfirmPassword} placeholder="请再次输入新密码" type="password" value={confirmPassword} />
          <button className="w-full rounded-xl bg-parcel-orange py-4 font-bold text-white shadow-lg shadow-parcel-orange/20 disabled:opacity-70" disabled={loading} type="submit">
            {loading ? "提交中..." : "重置密码"}
          </button>
          {error ? <p className="rounded-xl bg-error-container px-4 py-3 text-center text-sm font-semibold text-error">{error}</p> : null}
        </form>
      </section>
    </main>
  );
}

function LoginNetworkVisual() {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (window.matchMedia("(max-width: 767px), (prefers-reduced-motion: reduce)").matches) {
      return;
    }

    let active = true;
    let animationFrame = 0;
    let teardown: (() => void) | undefined;

    const loadThree = () => {
      if (window.THREE) {
        return Promise.resolve();
      }

      if (!window.__orcaparcelThreeLoader) {
        window.__orcaparcelThreeLoader = new Promise<void>((resolve, reject) => {
          const existing = document.querySelector<HTMLScriptElement>('script[data-orcaparcel-three="true"]');
          if (existing) {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener("error", () => reject(new Error("Three.js failed to load")), { once: true });
            return;
          }

          const script = document.createElement("script");
          script.src = "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";
          script.async = true;
          script.dataset.orcaparcelThree = "true";
          script.onload = () => resolve();
          script.onerror = () => reject(new Error("Three.js failed to load"));
          document.head.appendChild(script);
        });
      }

      return window.__orcaparcelThreeLoader;
    };

    loadThree()
      .then(() => {
        const container = containerRef.current;
        const THREE = window.THREE;

        if (!active || !container || !THREE) {
          return;
        }

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(60, 1, 0.1, 1000);
        camera.position.z = 200;

        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.domElement.setAttribute("aria-hidden", "true");
        renderer.domElement.className = "login-network-canvas";
        container.appendChild(renderer.domElement);

        const globeGroup = new THREE.Group();
        scene.add(globeGroup);

        const globeRadius = 80;
        const sphereGeo = new THREE.SphereGeometry(globeRadius, 32, 32);
        const sphereMat = new THREE.MeshBasicMaterial({
          color: 0x005bac,
          wireframe: true,
          transparent: true,
          opacity: 0.1
        });
        globeGroup.add(new THREE.Mesh(sphereGeo, sphereMat));

        const gridMat = new THREE.LineBasicMaterial({ color: 0x005bac, transparent: true, opacity: 0.2 });
        for (let i = 0; i <= 10; i += 1) {
          const latitude = (Math.PI / 10) * i;
          const points: any[] = [];
          for (let j = 0; j <= 64; j += 1) {
            const longitude = (2 * Math.PI / 64) * j;
            points.push(
              new THREE.Vector3(
                globeRadius * Math.sin(latitude) * Math.cos(longitude),
                globeRadius * Math.cos(latitude),
                globeRadius * Math.sin(latitude) * Math.sin(longitude)
              )
            );
          }
          globeGroup.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), gridMat));
        }

        const nodes: any[] = [];
        const nodeGeo = new THREE.SphereGeometry(0.9, 8, 8);
        const nodeMat = new THREE.MeshBasicMaterial({ color: 0x00a6d6 });
        const lineMat = new THREE.LineBasicMaterial({ color: 0x00a6d6, transparent: true, opacity: 0.28 });
        const nodeCount = 42;

        for (let i = 0; i < nodeCount; i += 1) {
          const phi = Math.acos(-1 + (2 * i) / nodeCount);
          const theta = Math.sqrt(nodeCount * Math.PI) * phi;
          const position = new THREE.Vector3(
            globeRadius * Math.cos(theta) * Math.sin(phi),
            globeRadius * Math.sin(theta) * Math.sin(phi),
            globeRadius * Math.cos(phi)
          );

          const node = new THREE.Mesh(nodeGeo, nodeMat);
          node.position.copy(position);
          globeGroup.add(node);
          nodes.push(position);

          if (i > 0 && i % 2 === 0) {
            const target = nodes[Math.max(0, i - 3)];
            globeGroup.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([position, target]), lineMat));
          }
        }

        const pulses = Array.from({ length: 12 }, (_, index) => {
          const mesh = new THREE.Mesh(
            new THREE.SphereGeometry(1.45, 8, 8),
            new THREE.MeshBasicMaterial({ color: index % 3 === 0 ? 0xff8a00 : 0x00a6d6 })
          );
          const start = nodes[index % nodes.length];
          const end = nodes[(index * 7 + 5) % nodes.length];
          mesh.position.copy(start);
          scene.add(mesh);
          return { mesh, start, end, t: (index % 5) / 5 };
        });

        const resize = () => {
          const width = Math.max(container.clientWidth, 320);
          const height = Math.max(container.clientHeight, 420);
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
          renderer.setSize(width, height, false);
        };

        resize();
        window.addEventListener("resize", resize);

        const animate = () => {
          animationFrame = window.requestAnimationFrame(animate);
          globeGroup.rotation.y += 0.002;
          globeGroup.rotation.x += 0.0008;

          pulses.forEach((pulse, index) => {
            pulse.t += 0.004 + index * 0.00012;
            if (pulse.t > 1) {
              pulse.t = 0;
              pulse.start = pulse.end;
              pulse.end = nodes[(index * 11 + Math.floor(Date.now() / 1600)) % nodes.length];
            }
            pulse.mesh.position.lerpVectors(pulse.start, pulse.end, pulse.t);
            const scale = 1 + Math.sin(Date.now() * 0.006 + index) * 0.22;
            pulse.mesh.scale.set(scale, scale, scale);
          });

          renderer.render(scene, camera);
        };

        animate();

        teardown = () => {
          window.cancelAnimationFrame(animationFrame);
          window.removeEventListener("resize", resize);
          scene.traverse((object: any) => {
            object.geometry?.dispose?.();
            if (Array.isArray(object.material)) {
              object.material.forEach((material: any) => material.dispose?.());
            } else {
              object.material?.dispose?.();
            }
          });
          renderer.dispose();
          renderer.domElement.remove();
        };
      })
      .catch(() => {
        // Keep the static CSS fallback if the CDN is unavailable.
      });

    return () => {
      active = false;
      teardown?.();
    };
  }, []);

  return <div className="login-network" ref={containerRef} aria-hidden="true" />;
}

function AuthField({
  label,
  icon,
  placeholder,
  type = "text",
  value,
  onChange,
  autoComplete
}: {
  label: string;
  icon: string;
  placeholder: string;
  type?: string;
  value?: string;
  onChange?: (value: string) => void;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="mb-1 block font-label-md text-label-md text-on-surface-variant">{label}</label>
      <div className="register-field-control group">
        <span className="material-symbols-outlined register-field-icon group-focus-within:text-secondary">{icon}</span>
        <input autoComplete={autoComplete} className="register-field-input" onChange={(event) => onChange?.(event.target.value)} placeholder={placeholder} type={type} value={value} />
      </div>
    </div>
  );
}

function AuthBox({
  label,
  icon,
  placeholder,
  value,
  onChange,
  autoComplete
}: {
  label: string;
  icon: string;
  placeholder: string;
  value?: string;
  onChange?: (value: string) => void;
  autoComplete?: string;
}) {
  return (
    <div className="space-y-2">
      <label className="block text-label-md text-on-surface">{label}</label>
      <div className="login-field-control group">
        <span className="material-symbols-outlined login-field-icon group-focus-within:text-secondary">{icon}</span>
        <input autoComplete={autoComplete} className="login-field-input" onChange={(event) => onChange?.(event.target.value)} placeholder={placeholder} type="text" value={value} />
      </div>
    </div>
  );
}
